import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';

const AdminProductAdd = () => {
  const navigate = useNavigate();
  
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form fields
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('Tees');
  const [basePrice, setBasePrice] = useState('');
  const [bgColor, setBgColor] = useState('#FFFFFF');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);

  const categories = ['Hats', 'Hoodies', 'Sweaters', 'Tees', 'Polos', 'Pants', 'Shorts', 'Outerwear', 'Womens', 'Youth'];

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
    
    // Create preview URLs
    const urls = files.map(file => URL.createObjectURL(file));
    setPreviewUrls(urls);
  };

  const handleSave = async () => {
    if (!name.trim() || !basePrice || selectedFiles.length === 0) {
      setError('NAME, PRICE, AND AT LEAST ONE IMAGE ARE REQUIRED');
      return;
    }
    
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      // Convert files to base64
      const imagePromises = selectedFiles.map(file => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      });
      
      const base64Images = await Promise.all(imagePromises);
      
      // Create product
      const productData = {
        name,
        description,
        category,
        base_price: parseFloat(basePrice),
        bg_color: bgColor,
        images: base64Images,
        variants: [], // Will be added later
        visible: true
      };
      
      await api.adminCreateProduct(productData);
      
      setSuccess('PRODUCT CREATED SUCCESSFULLY');
      
      // Clean up
      previewUrls.forEach(url => URL.revokeObjectURL(url));
      
      // Redirect after 2 seconds
      setTimeout(() => {
        navigate('/admin');
      }, 2000);
      
    } catch (err) {
      setError('FAILED TO CREATE PRODUCT');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#FEFEE3' }}>
      {/* Header */}
      <div style={{ background: '#FEFEE3', padding: '20px', borderBottom: '4px solid #000', boxShadow: '0 4px 0 0 #000' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button
            onClick={() => navigate('/admin')}
            className="retro-btn"
            style={{ background: '#FFF', border: '3px solid #000', color: '#2C2C2C', padding: '12px 20px', fontSize: '12px', boxShadow: '4px 4px 0 0 #000' }}
            data-testid="back-to-admin-button"
          >
            {'<<< BACK'}
          </button>
          
          <h1 style={{ fontSize: '20px', color: '#2C2C2C', letterSpacing: '0.12em' }}>[ ADD NEW PRODUCT ]</h1>
          
          <button
            onClick={handleSave}
            disabled={saving}
            className="retro-btn"
            style={{ 
              background: saving ? '#CCC' : '#2C6E49', 
              color: '#FEFEE3', 
              padding: '12px 24px', 
              fontSize: '12px',
              border: '3px solid #000',
              boxShadow: '4px 4px 0 0 #000',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.7 : 1
            }}
            data-testid="save-product-button"
          >
            {saving ? 'CREATING...' : 'CREATE >>>'}
          </button>
        </div>
      </div>
      
      {/* Main Content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '30px 20px' }}>
        
        {/* Status Messages */}
        {error && (
          <div style={{ background: '#FF5A5F', color: '#FFF', padding: '15px', marginBottom: '20px', border: '3px solid #000', boxShadow: '4px 4px 0 0 #000' }}>
            <p style={{ fontSize: '12px', fontWeight: 'bold', margin: 0 }}>{error}</p>
          </div>
        )}
        
        {success && (
          <div style={{ background: '#4AE54A', color: '#000', padding: '15px', marginBottom: '20px', border: '3px solid #000', boxShadow: '4px 4px 0 0 #000' }}>
            <p style={{ fontSize: '12px', fontWeight: 'bold', margin: 0 }}>{success}</p>
          </div>
        )}
        
        {/* Product Name */}
        <div style={{ background: '#FFF', padding: '20px', marginBottom: '20px', border: '3px solid #000', boxShadow: '4px 4px 0 0 #000' }}>
          <label htmlFor="name-input" style={{ fontSize: '12px', color: '#2C6E49', marginBottom: '10px', display: 'block', letterSpacing: '0.12em', fontWeight: 'bold' }}>
            [ PRODUCT NAME ] *
          </label>
          <input
            id="name-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{
              width: '100%',
              padding: '12px',
              fontSize: '14px',
              fontFamily: 'IBM Plex Mono, monospace',
              border: '3px solid #000',
              boxShadow: '3px 3px 0 0 #000',
              background: '#FEFEE3',
              color: '#2C2C2C'
            }}
            placeholder="THE LOOP GOLF POLO"
            data-testid="product-name-input"
          />
        </div>
        
        {/* Category & Price */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
          <div style={{ background: '#FFF', padding: '20px', border: '3px solid #000', boxShadow: '4px 4px 0 0 #000' }}>
            <label htmlFor="category-select" style={{ fontSize: '12px', color: '#2C6E49', marginBottom: '10px', display: 'block', letterSpacing: '0.12em', fontWeight: 'bold' }}>
              [ CATEGORY ]
            </label>
            <select
              id="category-select"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '14px',
                fontFamily: 'IBM Plex Mono, monospace',
                border: '3px solid #000',
                boxShadow: '3px 3px 0 0 #000',
                background: '#FEFEE3',
                color: '#2C2C2C'
              }}
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat.toUpperCase()}</option>
              ))}
            </select>
          </div>
          
          <div style={{ background: '#FFF', padding: '20px', border: '3px solid #000', boxShadow: '4px 4px 0 0 #000' }}>
            <label htmlFor="price-input" style={{ fontSize: '12px', color: '#2C6E49', marginBottom: '10px', display: 'block', letterSpacing: '0.12em', fontWeight: 'bold' }}>
              [ BASE PRICE ] *
            </label>
            <input
              id="price-input"
              type="number"
              step="0.01"
              value={basePrice}
              onChange={(e) => setBasePrice(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '14px',
                fontFamily: 'IBM Plex Mono, monospace',
                border: '3px solid #000',
                boxShadow: '3px 3px 0 0 #000',
                background: '#FEFEE3',
                color: '#2C2C2C'
              }}
              placeholder="49.99"
            />
          </div>
        </div>
        
        {/* Description */}
        <div style={{ background: '#FFF', padding: '20px', marginBottom: '20px', border: '3px solid #000', boxShadow: '4px 4px 0 0 #000' }}>
          <label htmlFor="description-input" style={{ fontSize: '12px', color: '#2C6E49', marginBottom: '10px', display: 'block', letterSpacing: '0.12em', fontWeight: 'bold' }}>
            [ DESCRIPTION ]
          </label>
          <textarea
            id="description-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            style={{
              width: '100%',
              padding: '15px',
              fontSize: '13px',
              fontFamily: 'IBM Plex Mono, monospace',
              border: '3px solid #000',
              boxShadow: '3px 3px 0 0 #000',
              background: '#FEFEE3',
              color: '#2C2C2C',
              resize: 'vertical',
              lineHeight: '1.6'
            }}
            placeholder="ENTER WITTY 8-BIT PRODUCT DESCRIPTION..."
          />
        </div>
        
        {/* Background Color */}
        <div style={{ background: '#FFF', padding: '20px', marginBottom: '20px', border: '3px solid #000', boxShadow: '4px 4px 0 0 #000' }}>
          <h2 style={{ fontSize: '12px', color: '#2C6E49', marginBottom: '15px', letterSpacing: '0.12em', fontWeight: 'bold' }}>
            [ BACKGROUND COLOR ]
          </h2>
          
          <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
            <input
              type="color"
              value={bgColor}
              onChange={(e) => setBgColor(e.target.value)}
              style={{
                width: '60px',
                height: '60px',
                border: '3px solid #000',
                boxShadow: '3px 3px 0 0 #000',
                cursor: 'pointer'
              }}
            />
            <input
              type="text"
              value={bgColor}
              onChange={(e) => setBgColor(e.target.value)}
              maxLength={7}
              style={{
                padding: '12px',
                fontSize: '14px',
                fontFamily: 'IBM Plex Mono, monospace',
                border: '3px solid #000',
                boxShadow: '3px 3px 0 0 #000',
                background: '#FEFEE3',
                color: '#2C2C2C',
                width: '150px'
              }}
              placeholder="#FFFFFF"
            />
          </div>
        </div>
        
        {/* Upload Images */}
        <div style={{ background: '#FFF', padding: '20px', marginBottom: '20px', border: '3px solid #000', boxShadow: '4px 4px 0 0 #000' }}>
          <h2 style={{ fontSize: '12px', color: '#2C6E49', marginBottom: '15px', letterSpacing: '0.12em', fontWeight: 'bold' }}>
            [ PRODUCT IMAGES ] *
          </h2>
          
          <p style={{ fontSize: '11px', color: '#4C956C', marginBottom: '15px' }}>
            >>> SELECT AT LEAST ONE IMAGE
          </p>
          
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileSelect}
            style={{
              padding: '12px',
              fontSize: '12px',
              fontFamily: 'IBM Plex Mono, monospace',
              border: '3px solid #000',
              boxShadow: '3px 3px 0 0 #000',
              background: '#FEFEE3',
              color: '#2C2C2C',
              width: '100%',
              cursor: 'pointer'
            }}
            data-testid="file-upload-input"
          />
          
          {/* Preview Images */}
          {previewUrls.length > 0 && (
            <div style={{ marginTop: '20px' }}>
              <p style={{ fontSize: '10px', color: '#4C956C', marginBottom: '10px' }}>
                SELECTED IMAGES ({previewUrls.length})
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '15px' }}>
                {previewUrls.map((url, idx) => (
                  <div 
                    key={idx}
                    style={{ 
                      border: '3px solid #4AE54A', 
                      boxShadow: '3px 3px 0 0 #000',
                      aspectRatio: '1/1',
                      overflow: 'hidden',
                      background: bgColor
                    }}
                  >
                    <img 
                      src={url} 
                      alt={`Preview ${idx + 1}`} 
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
      </div>
    </div>
  );
};

export default AdminProductAdd;
